import {TemplateRef} from '@angular/core';
import {
  InviteToolsResponse,
  TenderPhaseDetailsResponse,
  TenderPhaseType,
  TenderSelection,
  TenderStatus,
} from '@frontend/shared-api';
import {HrTool, HrToolProvider} from '@frontend/directus';
import {TimeframeDisplayVM} from './components/timeframe-display/timeframe-display.types';

export const enum CurrentPhaseStatus {
  LOADING = 'loading',
  PREVIOUS_PHASE_NOT_FINISHED = 'previous_phase_not_finished',
  SHOW_LIST = 'show_list',
}

export type InviteToolsResponseOf<T> = {
  tools: T[];
  currentPhase: TenderPhaseDetailsResponse;
  maxAllowedParticipants: number;
  calculatedPhaseSubmissionDate: string;
};

export interface InvitationTableData<T> {
  tools: T[];
  currentPhase: TenderPhaseDetailsResponse | null;
  maxAllowedParticipants: number;
  calculatedPhaseSubmissionDate: string | null;
}

export function emptyInvitationTableData<T>(): InvitationTableData<T> {
  return {
    tools: [],
    currentPhase: null,
    maxAllowedParticipants: 0,
    calculatedPhaseSubmissionDate: null,
  };
}

export interface ParticipatingToolsData {
  directusHrTools: HrTool[];
  participatingToolsResponse: InviteToolsResponse;
}

export type InvitationTableTemplateContext<T> = {
  $implicit: T;
  column: InvitationTableColumn<T>;
};

export interface InvitationTableColumn<T = BaseParticipatingTool> {
  field: string;
  header: string;
  headerTooltip?: string;
  cellTooltip?: (item: T) => string;
  sortable?: boolean;
  filter?: boolean;
  width?: string;
  template?: TemplateRef<InvitationTableTemplateContext<T>>;
  valueExtractor?: (item: T) => unknown;
  sortField?: string;
}

export interface InvitationTableQuickFilterOption {
  value: string;
  labelKey: string;
}

export interface InvitationTableQuickFilter<T = BaseParticipatingTool> {
  options: InvitationTableQuickFilterOption[];
  matches: (item: T, value: string) => boolean;
  searchText: (item: T) => string;
  searchPlaceholderKey: string;
  hintKey: string;
}

export interface InvitationTableQuickFilterSelectOption {
  value: string;
  label: string;
}

export interface InvitationTableConfig<T = BaseParticipatingTool> {
  columns: InvitationTableColumn<T>[];
  quickFilter?: InvitationTableQuickFilter<T>;
  emptyMessage: string;
  rows?: number;
  defaultSortField?: string;
  defaultSortOrder?: number; // 1 = ascending, -1 = descending
}

export interface PhaseInvitationConfig<T extends BaseParticipatingTool = BaseParticipatingTool> {
  phaseType: TenderPhaseType;
  timeFrameNumber: number;
  tableConfig: InvitationTableConfig<T>;
  timeframeConfig: TimeframeDisplayVM;
  prerequisiteChecker: () => boolean;
  stepLockedFn: () => boolean;
  isLoadingFn?: () => boolean;
  dataLoader: (projectId: number) => Promise<InviteToolsResponseOf<T>>;
  translationKeys: {
    title: string;
    rfiResultDisplayFn?: (item: T) => string;
  };
}

export interface ProviderDecisionReason {
  mainReasonId: string | null;
  detailReasonIds: string[];
  generatedText: string;
  comment: string | null;
  hasValue: boolean;
}

export interface BaseParticipatingTool {
  id: string; // directus id
  participatingToolId?: number; // BE id
  toolProvider: HrToolProvider;
  translatedName: string | null;
  selection?: TenderSelection | null;
  status?: TenderStatus;
  resultReview?: string;
  decisionReason?: ProviderDecisionReason | null;
}
